import React, { useEffect, useRef, useState } from 'react';
import { fmtDate } from '../shared/format';
import InfoTip from '../shared/InfoTip';

/* Each dropdown owns its open state locally + a scoped click-outside. Open state
 * is intentionally NOT lifted to the page: the campaign filters render in up to two
 * bar instances at once (the in-flow CampaignFilterBar + the header-portal
 * HeaderCampaignFilters during scroll promotion), so a single shared `open` boolean
 * made BOTH instances' menus open together (the duplicate-menu bug). Local state
 * scopes "open" to the one dropdown the user actually clicked. */
const useDropdownOpen = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);
  return { open, setOpen, ref };
};

/* Shared dropdown markup — status + campaign multi-select */
const StatusDropdown = ({ statusFilter, setStatusFilter, filterOptions, sizeClass }) => {
  const { open, setOpen, ref } = useDropdownOpen();
  return (
  <div className="relative" data-dropdown="status" ref={ref}>
    <button
      type="button"
      onClick={() => setOpen((isOpen) => !isOpen)}
      className={`${sizeClass} border dark:border-gray-700 rounded-lg px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 flex items-center gap-1`}
    >
      <span>{statusFilter === 'all' ? 'All Statuses' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}</span>
      <svg className="w-3 h-3 shrink-0 ml-1" viewBox="0 0 12 12"><path d="M3 5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" fill="none" /></svg>
    </button>
    {open && (
      <div className="absolute z-[9999] mt-1 left-0 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg shadow-lg py-1 min-w-[150px]">
        <button type="button" onClick={() => { setStatusFilter('all'); setOpen(false); }}
          className={`w-full text-left px-3 py-1 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 ${statusFilter === 'all' ? 'font-semibold text-[#32B1FF]' : 'text-gray-700 dark:text-gray-300'}`}>
          All Statuses
        </button>
        {(filterOptions?.campaign_statuses ?? []).map((status) => (
          <button key={status} type="button" onClick={() => { setStatusFilter(status.toLowerCase()); setOpen(false); }}
            className={`w-full text-left px-3 py-1 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 ${statusFilter === status.toLowerCase() ? 'font-semibold text-[#32B1FF]' : 'text-gray-700 dark:text-gray-300'}`}>
            {status.charAt(0) + status.slice(1).toLowerCase()}
          </button>
        ))}
      </div>
    )}
  </div>
  );
};

const CampaignDropdown = ({ selectedCampaigns, setSelectedCampaigns, filteredCampaignNames, sizeClass, maxW }) => {
  const { open, setOpen, ref } = useDropdownOpen();
  return (
  <div className="relative" data-dropdown="campaign" ref={ref}>
    <button
      type="button"
      onClick={() => setOpen((isOpen) => !isOpen)}
      className={`${sizeClass} border dark:border-gray-700 rounded-lg px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 ${maxW} truncate flex items-center gap-1`}
    >
      <span className="truncate">
        {selectedCampaigns.length === 0
          ? 'All Campaigns'
          : selectedCampaigns.length === 1
            ? selectedCampaigns[0]
            : `${selectedCampaigns.length} Campaigns`}
      </span>
      <svg className="w-3 h-3 shrink-0 ml-1" viewBox="0 0 12 12"><path d="M3 5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" fill="none" /></svg>
    </button>
    {open && (
      <div className="absolute z-[9999] mt-1 left-0 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg shadow-lg py-1 min-w-[220px] max-h-64 overflow-y-auto">
        <button
          type="button"
          onClick={() => setSelectedCampaigns([])}
          className={`w-full text-left px-3 py-1 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 ${selectedCampaigns.length === 0 ? 'font-semibold text-[#32B1FF]' : 'text-gray-700 dark:text-gray-300'}`}
        >
          All Campaigns
        </button>
        <div className="border-t dark:border-gray-700 my-0.5" />
        {filteredCampaignNames.map((name) => {
          const checked = selectedCampaigns.includes(name);
          return (
            <label
              key={name}
              className="flex items-center gap-2 px-3 py-1 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => {
                  setSelectedCampaigns((prev) =>
                    checked ? prev.filter((campaign) => campaign !== name) : [...prev, name]
                  );
                }}
                className="rounded border-gray-300 dark:border-gray-600 text-[#32B1FF] focus:ring-[#32B1FF]"
              />
              <span className="truncate">{name}</span>
            </label>
          );
        })}
      </div>
    )}
  </div>
  );
};

export const GlobalFilterBar = React.forwardRef(({
  localRangeStart, setLocalRangeStart, localRangeEnd, setLocalRangeEnd,
  showCampaignFiltersInHeader,
  statusFilter, setStatusFilter, filterOptions,
  selectedCampaigns, setSelectedCampaigns, filteredCampaignNames,
}, ref) => (
  <div ref={ref} className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-4 sticky top-0 z-30 overflow-visible">
    <div className="flex flex-col lg:flex-row gap-3 items-start lg:items-center">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <input type="date" className="w-[100px] sm:w-[110px] text-xs bg-transparent border-0 focus:outline-none text-gray-900 dark:text-gray-100 p-0"
            value={localRangeStart} max={localRangeEnd} onChange={(event) => setLocalRangeStart(event.target.value)} />
          <span className="text-gray-400">→</span>
          <input type="date" className="w-[100px] sm:w-[110px] text-xs bg-transparent border-0 focus:outline-none text-gray-900 dark:text-gray-100 p-0"
            value={localRangeEnd} min={localRangeStart} onChange={(event) => setLocalRangeEnd(event.target.value)} />
        </div>
        <div className="h-6 w-px bg-gray-200 dark:bg-gray-700"></div>
        <button type="button" onClick={() => { const today = new Date(); const start = new Date(today); start.setDate(today.getDate() - 7); setLocalRangeStart(fmtDate(start)); setLocalRangeEnd(fmtDate(today)); }}
          className="px-2.5 py-1.5 text-[13px] rounded-md bg-gray-50 dark:bg-gray-800 hover:bg-[#32B1FF] hover:text-white text-gray-600 dark:text-gray-400 transition-all">Week</button>
        <button type="button" onClick={() => { const today = new Date(); const start = new Date(today); start.setMonth(today.getMonth() - 1); setLocalRangeStart(fmtDate(start)); setLocalRangeEnd(fmtDate(today)); }}
          className="px-2.5 py-1.5 text-[13px] rounded-md bg-gray-50 dark:bg-gray-800 hover:bg-[#32B1FF] hover:text-white text-gray-600 dark:text-gray-400 transition-all">Month</button>
        <button type="button" onClick={() => { const today = new Date(); const start = new Date(today); start.setMonth(today.getMonth() - 3); setLocalRangeStart(fmtDate(start)); setLocalRangeEnd(fmtDate(today)); }}
          className="px-2.5 py-1.5 text-[13px] rounded-md bg-gray-50 dark:bg-gray-800 hover:bg-[#32B1FF] hover:text-white text-gray-600 dark:text-gray-400 transition-all">Quarter</button>
      </div>

      {showCampaignFiltersInHeader && (
        <>
          <div className="hidden lg:block h-6 w-px bg-gray-200 dark:bg-gray-700"></div>
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Campaign Performance</span>
          <StatusDropdown
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            filterOptions={filterOptions}
            sizeClass="text-xs"
          />
          <CampaignDropdown
            selectedCampaigns={selectedCampaigns}
            setSelectedCampaigns={setSelectedCampaigns}
            filteredCampaignNames={filteredCampaignNames}
            sizeClass="text-xs"
            maxW="max-w-[180px]"
          />
        </>
      )}
    </div>
  </div>
));

export const CampaignFilterBar = React.forwardRef(({
  statusFilter, setStatusFilter, filterOptions,
  selectedCampaigns, setSelectedCampaigns, filteredCampaignNames,
}, ref) => (
  <div ref={ref} className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-4 mt-4">
    <div className="flex flex-col lg:flex-row gap-3 items-start lg:items-center">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 inline-flex items-center">
        <span>Campaign Performance</span>
        <InfoTip text="Per-campaign analysis. Use the status and campaign filters above to scope the spend trend, search terms, and campaign table below." />
      </h2>
      <div className="hidden lg:block h-6 w-px bg-gray-200 dark:bg-gray-700"></div>
      <StatusDropdown
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        filterOptions={filterOptions}
        sizeClass="text-sm"
      />
      <CampaignDropdown
        selectedCampaigns={selectedCampaigns}
        setSelectedCampaigns={setSelectedCampaigns}
        filteredCampaignNames={filteredCampaignNames}
        sizeClass="text-sm"
        maxW="max-w-[240px]"
      />
    </div>
  </div>
));

/* Reads the app header height (published as a CSS var by Admin/ClientHeader) so the
 * scroll threshold matches the bottom of the real sticky header. Falls back to 72px. */
const getHeaderBottom = () => {
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--app-header-h');
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : 72;
};

/* True while the campaign-performance group is scrolled under the header — i.e. the in-flow
 * filter bar has passed above the header bottom but the group hasn't ended yet. Drives
 * promoting the filters into the header (single-client view) or the GlobalFilterBar (all-clients).
 * When globalFilterRef is absent the threshold is the app header bottom; groupEndRef releases it
 * once the next section (Dimensional Breakdowns) reaches the header. */
export const useCampaignFilterScrollShadow = (globalFilterRef, campaignFilterRef, groupEndRef) => {
  const [showCampaignFiltersInHeader, setShowCampaignFiltersInHeader] = useState(false);

  useEffect(() => {
    const checkScroll = () => {
      if (!campaignFilterRef.current) return;
      const headerBottom = globalFilterRef?.current
        ? globalFilterRef.current.getBoundingClientRect().bottom
        : getHeaderBottom();
      // Promote only once the in-flow bar has fully scrolled behind the header (bottom edge,
      // not top) so the page copy is occluded by the opaque header — never both at once.
      const pastStart = campaignFilterRef.current.getBoundingClientRect().bottom <= headerBottom;
      const beforeEnd = groupEndRef?.current
        ? groupEndRef.current.getBoundingClientRect().top > headerBottom
        : true;
      setShowCampaignFiltersInHeader(pastStart && beforeEnd);
    };
    window.addEventListener('scroll', checkScroll, true);
    window.addEventListener('resize', checkScroll);
    checkScroll();
    return () => {
      window.removeEventListener('scroll', checkScroll, true);
      window.removeEventListener('resize', checkScroll);
    };
  }, [globalFilterRef, campaignFilterRef, groupEndRef]);

  return showCampaignFiltersInHeader;
};

/* Compact campaign filters for injecting into the app header's center slot (via portal)
 * once the in-flow CampaignFilterBar scrolls under the header. Keeps a single filter bar
 * on screen instead of stacking a second sticky bar under the header. */
export const HeaderCampaignFilters = ({
  statusFilter, setStatusFilter, filterOptions,
  selectedCampaigns, setSelectedCampaigns, filteredCampaignNames,
}) => (
  // No "Campaign Performance" label here — it's redundant (the in-page bar carries
  // it) and eats ~150px that made the header portal collide with the org selector.
  <div className="flex items-center gap-2 min-w-0">
    <StatusDropdown
      statusFilter={statusFilter}
      setStatusFilter={setStatusFilter}
      filterOptions={filterOptions}
      sizeClass="text-sm"
    />
    <CampaignDropdown
      selectedCampaigns={selectedCampaigns}
      setSelectedCampaigns={setSelectedCampaigns}
      filteredCampaignNames={filteredCampaignNames}
      sizeClass="text-sm"
      maxW="max-w-[200px]"
    />
  </div>
);
